import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { StaffShell } from "@/components/layout/StaffShell";
import { adminNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CenterSpinner } from "@/components/common/States";

export default function AdminHeroStats() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-hero-stats"], queryFn: () => api("/admin/hero-stats") });
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data) setStats(data);
  }, [data]);

  const update = (i, k, v) => setStats((s) => s.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setStats((s) => [...s, { key: `stat${s.length + 1}`, value: "", label_en: "", label_af: "", visible: true }]);
  const remove = (i) => setStats((s) => s.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      await api("/site/hero-stats", { method: "PUT", json: { stats } });
      toast.success("Hero stats saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <StaffShell
      items={adminNav}
      roleLabel="Admin"
      title="Hero stats"
      headerRight={<Button className="rounded-full" onClick={save} disabled={busy || !stats} data-testid="save-hero-button">Save</Button>}
    >
      {isLoading || !stats ? (
        <CenterSpinner />
      ) : (
        <div className="mx-auto max-w-2xl space-y-4">
          <p className="text-sm text-muted-foreground">These appear on the customer home hero banner.</p>
          {stats.map((s, i) => (
            <Card key={i} className="space-y-3 rounded-2xl p-5" data-testid={`hero-stat-${i}`}>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Value</Label><Input value={s.value} onChange={(e) => update(i, "value", e.target.value)} placeholder="25+" /></div>
                <div className="space-y-1.5"><Label>Key</Label><Input value={s.key} onChange={(e) => update(i, "key", e.target.value)} /></div>
                <div className="space-y-1.5 col-span-2"><Label>Label</Label><Input value={s.label_en} onChange={(e) => update(i, "label_en", e.target.value)} /></div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch checked={s.visible} onCheckedChange={(v) => update(i, "visible", v)} />
                  <Label className="text-sm">Visible</Label>
                </div>
                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </Card>
          ))}
          {stats.length < 8 && (
            <Button variant="outline" className="w-full rounded-full" onClick={add} data-testid="add-hero-stat"><Plus className="mr-1 h-4 w-4" /> Add stat</Button>
          )}
        </div>
      )}
    </StaffShell>
  );
}
